import { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Image,
  useBreakpointValue,
} from '@chakra-ui/react';

type Props = {
  workspaceName?: string;
  workspaceLogo?: string;
  workspaceBannerImage?: string;
  children: ReactNode;
};

export default function WorkspaceShell({
  workspaceName,
  workspaceLogo,
  workspaceBannerImage,
  children,
}: Props) {
  const compactBanner = useBreakpointValue({ base: true, md: false });
  const displayName = workspaceName || 'Workspace';
  const hasLogo = Boolean(workspaceLogo);

  return (
    <Box minH='100vh' bg='secondary.500' className='print-layout'>
      <Box as='header' className='no-print' position='sticky' top={0} zIndex={10}>
        <Container maxW='6xl' px={{ base: 0, md: 6 }} pt={{ base: 0, md: 4 }}>
          <Flex
            bg='white'
            borderWidth='1px'
            borderColor='gray.200'
            borderRadius={{ base: 0, md: 'xl' }}
            shadow='sm'
            px={{ base: 4, md: 6 }}
            py={3}
            align='center'
            justify='space-between'
          >
            <NavLink to='/'>
              {hasLogo ? (
                <Image
                  src={workspaceLogo}
                  alt={displayName}
                  maxH='32px'
                  w='auto'
                  objectFit='contain'
                />
              ) : (
                <Heading size='sm' color='gray.800'>
                  {displayName}
                </Heading>
              )}
            </NavLink>
            <Button size='sm' variant='outline' colorPalette='gray' asChild>
              <NavLink to='/auth/login'>Sign in</NavLink>
            </Button>
          </Flex>
        </Container>
      </Box>

      <Box className='no-print' position='relative'>
        <Container maxW='6xl' px={{ base: 0, md: 6 }} pt={{ base: 0, md: 4 }} pb={{ base: 4, md: 6 }}>
          <Box
            position='relative'
            minH={{ base: '160px', md: compactBanner ? '180px' : '220px' }}
            borderRadius={{ base: 0, md: '2xl' }}
            overflow='hidden'
            bg='brand.solid'
            backgroundImage={`url(${workspaceBannerImage || '/banner-default.png'})`}
            backgroundSize='cover'
            backgroundPosition='center'
            shadow={{ base: 'none', md: 'lg' }}
          >
            <Box
              position='absolute'
              inset={0}
              background='linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.45) 100%)'
              pointerEvents='none'
            />
            <Flex
              position='relative'
              direction='column'
              align='center'
              justify='center'
              h='100%'
              minH='inherit'
              py={{ base: 8, md: 10 }}
              gap={3}
            >
              {hasLogo && (
                <Box
                  w={{ base: '112px', md: '144px' }}
                  h={{ base: '112px', md: '144px' }}
                  bg='white'
                  borderRadius='lg'
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                  shadow='0 8px 24px rgba(0,0,0,0.25)'
                  overflow='hidden'
                >
                  <Image
                    src={workspaceLogo}
                    alt={displayName}
                    maxH='80%'
                    maxW='80%'
                    objectFit='contain'
                  />
                </Box>
              )}
            </Flex>
          </Box>
        </Container>
      </Box>

      {children}
    </Box>
  );
}
